<script setup lang="ts">
import UserCard from '@/components/Manager/UserCard.vue';
import { useManager } from '@/composables/useManager';
import { useSubscription } from '@/composables/useSubscription';
import { useUserStore } from '@/stores/useUserStore';
import type { Employee } from '@/types/api';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const toast = useToast();
const userStore = useUserStore();
const user = ref(userStore.user);
const { getTeam } = useManager();
const { t } = useI18n();
const router = useRouter();

const team = ref<Employee[]>([]);
const loading = ref(true); // Add loading state

const { maxEmployeesAllowed, canAddEmployee, isSubscribed, ensureTeamDataLoaded } = useSubscription();

const currentEmployeeCount = computed(() => {
  // employees already excludes the current user.
  // pendingTeam includes users invited but not yet registered.
  return employees.value.length + pendingTeam.value.length;
});

const employees = computed(() => {
  return team.value.filter((member) => member.registration_completed && member.id !== user.value?.id && member.role === 'employee');
});

const admins = computed(() => {
  return team.value.filter(
    (member) => member.registration_completed && member.id !== user.value?.id && (member.role === 'admin' || member.role === 'manager'),
  );
});

const currentUser = computed(() => {
  return team.value.find((member) => member.id === user.value?.id);
});

const pendingTeam = computed(() => {
  return team.value.filter((user) => !user.registration_completed);
});

async function fetchTeam() {
  if (!user.value || !user.value.active_restaurant) return;

  loading.value = true;
  try {
    const { data, error } = await getTeam(user.value.active_restaurant.id);

    if (error) {
      console.error(error);
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: "Une erreur est survenue lors de la récupération de l'équipe",
        life: 3000,
      });
      return;
    }

    team.value = data.users;
  } catch (err) {
    console.error('Error fetching team:', err);
  } finally {
    loading.value = false;
  }
}

function addEmployee() {
  if (!canAddEmployee.value) {
    toast.add({
      severity: 'warn',
      summary: t('team.notifications.limitReachedTitle'), // Suggested new i18n key
      detail: t('team.notifications.limitReachedDetail', { count: maxEmployeesAllowed.value }), // Suggested new i18n key
      life: 4000,
    });
    return;
  }
  router.push({ name: 'add-employee' });
}

function goToPlanning(tabIndex: number) {
  localStorage.setItem('planningActiveTab', tabIndex.toString());
  router.push({ name: 'planning' });
}

// Fetch team data when component is mounted
onMounted(async () => {
  await ensureTeamDataLoaded();
  fetchTeam();
});
</script>

<template>
  <main>
    <div class="flex flex-row items-center justify-end gap-4">
      <!-- <p class="text-sm text-primary-900">Accès rapide</p> -->
      <Button outlined icon="pi pi-arrow-up-right" :label="t('planning.tabs.presences')" @click="goToPlanning(1)" />
      <Button outlined icon="pi pi-arrow-up-right" :label="t('planning.tabs.tasks')" @click="goToPlanning(2)" />
      <Button
        outlined
        icon="pi pi-calendar"
        :label="t('profile.profile.profileTab.dayOff.title')"
        @click="router.push({ name: 'day-off-approvals' })"
      />
    </div>
    <div class="mt-6 flex flex-col gap-6">
      <div>
        <UserCard v-if="currentUser" :user="currentUser" />
      </div>
      <div v-if="admins.length > 0" id="admins">
        <h1 class="mb-6">{{ t('team.adminList') }}</h1>
        <div v-if="loading">{{ t('team.loadingAdmins') }}</div>
        <div v-else-if="admins.length === 0">{{ t('team.noAdmins') }}</div>
        <div v-else class="space-y-4">
          <UserCard v-for="theUser in admins" :key="theUser.id" :user="theUser" />
        </div>
      </div>
      <div id="team">
        <h1 class="mb-6">{{ t('team.employeeList') }}</h1>
        <div v-if="loading">{{ t('team.loadingTeams') }}</div>
        <div v-else-if="employees.length === 0">{{ t('team.noEmployees') }}</div>
        <div v-else class="space-y-4">
          <UserCard v-for="theUser in employees" :key="theUser.id" :user="theUser" />
        </div>
        <div class="onboarding-add-team-member-button mt-4">
          <p class="inline-button" :class="{ 'cursor-not-allowed opacity-50': !canAddEmployee }" @click="addEmployee">
            + {{ t('team.addEmployee') }} <span v-if="maxEmployeesAllowed < 999">({{ currentEmployeeCount }} / {{ maxEmployeesAllowed }})</span>
          </p>
          <p v-if="!canAddEmployee && isSubscribed" class="text-sm italic text-primary-900">
            {{
              t('team.notifications.employeeLimitReachedInfo', {
                current: currentEmployeeCount,
                max: maxEmployeesAllowed,
              })
            }}
          </p>
          <p v-if="!isSubscribed" class="text-warn-500 mt-2 text-sm">
            {{ t('team.notifications.subscriptionRequiredToAddEmployee') }}
          </p>
        </div>
      </div>
      <div id="pending">
        <h1 class="mb-6">{{ t('team.pending') }}</h1>
        <div v-if="loading">{{ t('team.loadingPending') }}</div>
        <div v-else-if="pendingTeam.length === 0">{{ t('team.noPending') }}</div>
        <div v-else class="space-y-4">
          <UserCard v-for="theUser in pendingTeam" :key="theUser.id" :user="theUser" />
        </div>
      </div>
    </div>
  </main>
</template>
